import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { KioskSubmission } from '@ecc/shared';
import { AcsButton, AcsCard, AcsInput, AcsLogo } from '../components';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useI18n } from '../i18n/I18nProvider';
import {
  KioskNotPairedError,
  OperatorSessionActiveError,
  fetchKioskConfig,
  pairDevice,
  type KioskConfig,
} from './api';
import { KioskForm, type KioskFormValues } from './KioskForm';
import { LocalPersistenceError, countUnsynced, enqueue } from './queue';
import { startAutoSync, syncPending } from './sync';
import './kiosk.css';

/**
 * Modalità kiosk (§7.1).
 *
 * Flusso: dispositivo associato → form → salvataggio locale → successo →
 * sincronizzazione → reset completo.
 *
 * Il successo viene mostrato **solo** se il salvataggio locale è riuscito. Se
 * fallisce anche quello, il visitatore riceve un messaggio diverso e viene
 * indirizzato allo staff: mai dichiarare registrata una richiesta che non lo è
 * (§26).
 */

type Screen =
  | { name: 'loading' }
  | { name: 'pairing'; error?: string }
  /** Sessione operatore attiva nello stesso browser: serve un'altra finestra. */
  | { name: 'operator-session' }
  | { name: 'form' }
  | { name: 'success'; offline: boolean }
  | { name: 'failed' };

export function KioskPage() {
  const { t, language } = useI18n();
  const [config, setConfig] = useState<KioskConfig | null>(null);
  const [screen, setScreen] = useState<Screen>({ name: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const loadConfig = useCallback(async () => {
    try {
      const loaded = await fetchKioskConfig(language);
      setConfig(loaded);
      setScreen({ name: 'form' });
    } catch (error) {
      if (error instanceof OperatorSessionActiveError) {
        setScreen({ name: 'operator-session' });
        return;
      }
      if (error instanceof KioskNotPairedError) {
        setScreen({ name: 'pairing' });
        return;
      }
      setScreen({ name: 'pairing', error: t.errors.unexpected });
    }
  }, [language, t.errors.unexpected]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  // Sincronizzazione opportunistica in sottofondo: non blocca mai la raccolta.
  useEffect(() => {
    const refreshCount = () => void countUnsynced().then(setPendingCount);
    const stop = startAutoSync(30_000, refreshCount);
    refreshCount();
    return stop;
  }, []);

  async function handlePair(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const token = String(data.get('token') ?? '').trim();

    try {
      await pairDevice(token);
      await loadConfig();
    } catch {
      setScreen({ name: 'pairing', error: t.kiosk.pairFailed });
    }
  }

  async function handleSubmit(values: KioskFormValues): Promise<void> {
    setSubmitting(true);

    const submission: KioskSubmission = {
      ...values,
      // UUID generato dal client: chiave di idempotenza del sync (§9.3).
      submissionId: crypto.randomUUID(),
      submittedAt: new Date().toISOString(),
    };

    try {
      // 1. Persistenza locale. Se fallisce, non si mostra alcun successo.
      await enqueue(submission);
    } catch (error) {
      setSubmitting(false);
      if (error instanceof LocalPersistenceError) {
        setScreen({ name: 'failed' });
        return;
      }
      setScreen({ name: 'failed' });
      return;
    }

    // 2. Successo immediato: il visitatore non attende la rete (§9.1).
    const report = await syncPending().catch(() => null);
    setSubmitting(false);
    setScreen({ name: 'success', offline: report === null || report.synced === 0 });
    void countUnsynced().then(setPendingCount);
  }

  /** Reset completo: il form riparte vuoto, senza traccia del visitatore precedente (§7.3). */
  function startNewRequest(): void {
    setScreen({ name: 'form' });
  }

  const header = (
    <>
      <AcsLogo />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--acs-space-4)' }}>
        {pendingCount > 0 ? (
          <span className="kiosk-pending">
            {t.kiosk.pendingNotice} {pendingCount}
          </span>
        ) : null}
        <LanguageSwitcher />
      </div>
    </>
  );

  return (
    <div className="kiosk-shell">
      <header className="kiosk-shell__header">{header}</header>
      <main className="kiosk-shell__main">
        {screen.name === 'loading' ? <p>{t.common.loading}</p> : null}

        {screen.name === 'pairing' ? (
          <AcsCard>
            <h1 style={{ marginTop: 0 }}>{t.kiosk.pairTitle}</h1>
            <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.kiosk.notConfigured}</p>
            <form onSubmit={(e) => void handlePair(e)}>
              <AcsInput
                label={t.kiosk.pairToken}
                name="token"
                autoComplete="off"
                required
                error={screen.error}
              />
              <AcsButton type="submit" block>
                {t.kiosk.pairSubmit}
              </AcsButton>
            </form>
          </AcsCard>
        ) : null}

        {screen.name === 'operator-session' ? (
          <AcsCard>
            <h1 style={{ marginTop: 0 }}>{t.kiosk.operatorSessionTitle}</h1>
            <p>{t.kiosk.operatorSessionBody}</p>
            <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.kiosk.operatorSessionHint}</p>
          </AcsCard>
        ) : null}

        {screen.name === 'form' && config ? (
          <>
            <EventHero config={config} />
            <KioskForm
              config={config}
              submitting={submitting}
              onSubmit={(values) => void handleSubmit(values)}
            />
          </>
        ) : null}

        {screen.name === 'success' ? (
          <div className="kiosk-success">
            <h1 className="kiosk-success__title">✓ {t.kiosk.successTitle}</h1>
            <p>{t.kiosk.successThanks}</p>
            {/*
              Testo neutro: non dice né lascia intendere che il visitatore si sia
              iscritto a qualcosa, perché potrebbe non aver prestato consenso (§7.4).
            */}
            <p>{t.kiosk.successBody}</p>
            {screen.offline ? <p className="kiosk-success__note">{t.kiosk.savedOffline}</p> : null}
            <AcsButton onClick={startNewRequest}>{t.kiosk.newRequest}</AcsButton>
          </div>
        ) : null}

        {screen.name === 'failed' ? (
          <div className="kiosk-success">
            <h1 className="kiosk-success__title">{t.kiosk.notSaved}</h1>
            <AcsButton onClick={startNewRequest}>{t.kiosk.newRequest}</AcsButton>
          </div>
        ) : null}
      </main>
    </div>
  );
}

/** Visual della fiera: branding, mai un elemento che comprima i controlli privacy (§20.4). */
function EventHero({ config }: { config: KioskConfig }) {
  const { branding, name } = config.event;
  const hasContent = branding.headline || branding.shortDescription || branding.imageAssetId;
  if (!hasContent) return null;

  return (
    <div className="kiosk-hero">
      {branding.imageAssetId ? (
        <img
          className="kiosk-hero__image"
          src={`/api/assets/${branding.imageAssetId}`}
          alt={branding.imageAltText ?? ''}
        />
      ) : null}
      <div className="kiosk-hero__text">
        <h2 className="kiosk-hero__headline">{branding.headline ?? name}</h2>
        {branding.shortDescription ? (
          <p className="kiosk-hero__description">{branding.shortDescription}</p>
        ) : null}
      </div>
    </div>
  );
}
